import React, { useState } from 'react'
import PerfectScrollbar from 'react-perfect-scrollbar'

import useSnackbar from 'hooks/useSnackbar'
import usePermissions from 'hooks/usePermissions'
import useAuth from 'hooks/useAuth'

import { Box, Button, CircularProgress, Typography } from '@material-ui/core'
import { PlusCircle } from 'react-feather'

import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from 'components/Table'
import NonComplianceReportsActionModal from '../NonComplianceReportsActionModal'
import { MenuButton } from 'components'
import DialogRemoveNonComplianceReportAction from '../DialogRemoveNonComplianceReportAction'

import * as service from 'service'

import useRncAction from 'hooks/useRncAction'

import helpers from 'helpers'
import constants from 'constants/index'

const TableRowNonComplianceReportsAction = ({
  nonComplianceReportAction,
  nonComplianceReport,
  isLoading,
  viewOnly,
  setDialogOpen = () => {},
  setNonComplianceReportAction = () => {},
  refresh = () => {},
  handleOpenEditDialog = () => {},
  handleOpenRemoveDialog = () => {},
  handleNonComplianceReportsActionsPermmited,
  user,
  ...rest
}) => {
  return (
    <>
      {isLoading ? (
        <Box
          display="flex"
          width="100%"
          minHeight="700px"
          justifyContent="center"
          alignItems="center"
        >
          <CircularProgress />
        </Box>
      ) : (
        <TableRow {...rest}>
          <TableCell>{nonComplianceReportAction?.id}</TableCell>
          <TableCell>
            {nonComplianceReportAction?.responsableUser?.name || '-'}
          </TableCell>
          <TableCell>
            {nonComplianceReportAction?.correctiveAction || '-'}
          </TableCell>
          <TableCell>
            {nonComplianceReportAction?.correctionEvidence || '-'}
          </TableCell>
          <TableCell>
            {helpers.formatters.date(nonComplianceReportAction?.deadlineDate)}
          </TableCell>
          <TableCell align="right">
            {!viewOnly &&
              (handleNonComplianceReportsActionsPermmited ||
                nonComplianceReportAction?.responsableUser?.id === user.id) && (
                <MenuButton>
                  <Button
                    color="secondary"
                    fullWidth
                    onClick={() =>
                      handleOpenEditDialog(nonComplianceReportAction)
                    }
                  >
                    Editar
                  </Button>
                  <Button
                    color="secondary"
                    fullWidth
                    onClick={() =>
                      handleOpenRemoveDialog(nonComplianceReportAction)
                    }
                  >
                    Excluir
                  </Button>
                </MenuButton>
              )}
          </TableCell>
        </TableRow>
      )}
    </>
  )
}

const NonComplianceReportsActionTable = ({
  nonComplianceReportId,
  nonComplianceReport,
  isLoading,
  viewOnly,
}) => {
  const [edit, setEdit] = useState(false)
  const [nonComplianceReportAction, setNonComplianceReportAction] = useState({})
  const [nonComplianceReportActionModal, setNonComplianceReportActionModal] =
    useState(false)
  const [
    nonComplianceReportActionRemoveDialog,
    setNonComplianceReportActionRemoveDialog,
  ] = useState(false)
  const [loading, setLoading] = useState(false)
  const snackbar = useSnackbar()
  const { permitted } = usePermissions()
  const { user } = useAuth()

  const handleNonComplianceReportsActionsPermmited = permitted(
    constants.permissions.INCIDENTS.MANAGE,
  )

  const { data: rncActions, loadData: loadRncActions } = useRncAction()

  const onRefresh = async () => {
    await loadRncActions(nonComplianceReportId)
  }

  const handleRemove = async (
    nonComplianceReportId,
    nonComplianceReportActionId,
  ) => {
    try {
      setLoading(true)
      await service.dponet.nonComplianceReportActions.destroy({
        nonComplianceReportId,
        nonComplianceReportActionId,
      })
      setNonComplianceReportActionRemoveDialog(false)
      setLoading(false)
      snackbar.open({
        message: 'Plano de ação removido com sucesso!',
        variant: 'success',
      })

      onRefresh()
    } catch (err) {
      setLoading(false)
      snackbar.open({
        message: 'Não foi possível remover o Plano de ação. Tente novamente!',
        variant: 'error',
      })
    }
  }

  const handleOpenCreateDialog = () => {
    setNonComplianceReportAction({})
    setNonComplianceReportActionModal(true)
    setEdit(false)
  }

  const handleOpenEditDialog = (nonComplianceReportAction) => {
    setNonComplianceReportAction(nonComplianceReportAction)
    setNonComplianceReportActionModal(true)
    setEdit(true)
  }

  const handleOpenRemoveDialog = (nonComplianceReportAction) => {
    setNonComplianceReportAction(nonComplianceReportAction)
    setNonComplianceReportActionRemoveDialog(true)
  }

  const isResponsable = () => {
    return rncActions
      .map((rncAction) => {
        return rncAction?.responsableUser?.id
      })
      .includes(user.id)
  }

  return (
    <>
      {isLoading || loading ? (
        <Box
          display="flex"
          width="100%"
          minHeight="700px"
          justifyContent="center"
          alignItems="center"
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box width="100%">
          <Box
            mt={3}
            mb={2}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h5">Planos de ação</Typography>
            {!viewOnly && handleNonComplianceReportsActionsPermmited && (
              <Button
                variant="outlined"
                startIcon={<PlusCircle size={20} />}
                htmlFor="rnc_action"
                component="label"
                onClick={() => handleOpenCreateDialog()}
                id={constants.incidents.EDIT_INCIDENT_DRIVER_STEP_5}
              >
                <Box mr={1}>
                  <Typography>Adicionar plano de ação</Typography>
                </Box>
              </Button>
            )}
          </Box>
          <PerfectScrollbar
            options={{ wheelPropagation: true, useBothWheelAxes: true }}
          >
            <Table
              size="small"
              emptyMessage="Nenhum plano de ação deste registro de melhoria contínua foi encontrado"
              isLoading={isLoading}
              noWrap
            >
              <TableHead>
                <TableRow>
                  <TableCell width="8%">Identificador</TableCell>
                  <TableCell width="15%">Responsável</TableCell>
                  <TableCell width={viewOnly ? '33%' : '30%'}>
                    Ação corretiva
                  </TableCell>
                  <TableCell width={viewOnly ? '33%' : '30%'}>
                    Evidência da implantação
                  </TableCell>
                  <TableCell width="11%">Prazo</TableCell>
                  {!viewOnly &&
                    (handleNonComplianceReportsActionsPermmited ||
                      isResponsable()) && (
                      <TableCell width="6%" align="right">
                        Ações
                      </TableCell>
                    )}
                </TableRow>
              </TableHead>
              <TableBody>
                {rncActions?.map((nonComplianceReportAction) => (
                  <TableRowNonComplianceReportsAction
                    key={nonComplianceReportAction.id}
                    isLoading={loading || isLoading}
                    nonComplianceReportAction={nonComplianceReportAction}
                    setNonComplianceReportAction={setNonComplianceReportAction}
                    refresh={onRefresh}
                    viewOnly={viewOnly}
                    nonComplianceReport={nonComplianceReport}
                    handleOpenEditDialog={handleOpenEditDialog}
                    handleOpenRemoveDialog={handleOpenRemoveDialog}
                    handleNonComplianceReportsActionsPermmited={
                      handleNonComplianceReportsActionsPermmited
                    }
                    user={user}
                  />
                ))}
              </TableBody>
            </Table>
          </PerfectScrollbar>
        </Box>
      )}

      <NonComplianceReportsActionModal
        nonComplianceReportId={nonComplianceReportId}
        open={nonComplianceReportActionModal}
        setOpen={setNonComplianceReportActionModal}
        nonComplianceReportAction={nonComplianceReportAction}
        onEvent={onRefresh}
        edit={edit}
      />
      <DialogRemoveNonComplianceReportAction
        open={nonComplianceReportActionRemoveDialog}
        setOpen={setNonComplianceReportActionRemoveDialog}
        handleRemove={handleRemove}
        nonComplianceReportId={nonComplianceReportId}
        nonComplianceReportActionId={nonComplianceReportAction?.id}
      />
    </>
  )
}

export default NonComplianceReportsActionTable
